import { Component, Input, OnDestroy, OnInit, AfterViewChecked, ElementRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ILocalization, INews } from 'src/app/pages/actualites/interfaces/news';
import { NewsService } from 'src/app/pages/actualites/news.service';
import { BreadcrumbService } from 'src/app/services/breadcrumb.service';
import { StrapiContentCleanerService } from 'src/app/services/strapi-content-cleaner/strapi-content-cleaner.service';
import { StrapiContentAccessibilityService } from 'src/app/services/strapi-content-accessibility/strapi-content-accessibility.service';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import * as qs from 'qs';
import { Observable, of, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
@Component({
  selector: 'app-actualite-detail',
  templateUrl: './actualite-detail.component.html',
  styleUrls: ['./actualite-detail.component.scss'],
  //ncapsulation: ViewEncapsulation.None,
})
export class ActualiteDetailComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() format?: string;
  private subscriptionRemover = new Subject<void>();
  private params?: string;
  public article$?: Observable<INews>;
  public locale!: string;
  public localization!: Array<ILocalization>;
  private isContentCleaned = false;
  private isAccessibilityApplied = false;

  constructor(
    private route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private newsService: NewsService,
    private translate: TranslateService,
    private router: Router,
    private elementRef: ElementRef,
    private strapiCleaner: StrapiContentCleanerService,
    private strapiAccessibility: StrapiContentAccessibilityService
  ) {
    this.locale = this.translate.currentLang;
    this.breadcrumbService.setItems([
      { label: 'menu.sInformer' },
      {
        label: 'menu.actualites',
        routerLink: 'ROUTES.actualites',
        visibleMobile: true,
      },
    ]);
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.subscriptionRemover))
      .subscribe((params) => (this.params = params.slug));

    this.router.events
      .pipe(
        takeUntil(this.subscriptionRemover),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.isContentCleaned = false; // Reset pour permettre le nettoyage du nouveau contenu
          this.isAccessibilityApplied = false;
          this.article$ = this.getArticles();
        }, 300);
      });

    this.translate.onLangChange
      .pipe(takeUntil(this.subscriptionRemover))
      .subscribe((langChangeEvent: LangChangeEvent) => {
        this.locale = langChangeEvent.lang;
        const slug = this.localization.find(
          (data) => data.attributes.locale === this.locale
        )?.attributes.slug;

        this.router.navigate([
          '/',
          this.locale,
          this.translate.instant('ROUTES.actualites'),
          slug,
        ]);
      });

    setTimeout(() => {
      this.article$ = this.getArticles();
    }, 300);
}

  ngAfterViewChecked(): void {
    // Workaround temporaire pour nettoyer le contenu Strapi
    // Voir StrapiContentCleanerService pour plus de détails
    if (!this.isContentCleaned) {
      this.cleanEmptyParagraphs();
    }
    if (!this.isAccessibilityApplied) {
      this.applyAccessibilityFixes();
    }
  }

  /**
   * Nettoie les paragraphes vides générés par Strapi
   * Délègue le traitement au StrapiContentCleanerService
   * 
   * TEMPORAIRE - Peut être supprimé quand Strapi sera amélioré
   */
  private cleanEmptyParagraphs(): void {
    const container = this.elementRef.nativeElement.querySelector('.article-container');
    if (!container) return;

    const hasCleanedSomething = this.strapiCleaner.cleanStrapiContent(container);
    
    if (hasCleanedSomething) {
      this.isContentCleaned = true;
    }
  }

  /**
   * Applique les corrections d'accessibilité RGAA sur le contenu Strapi
   * Délègue le traitement au StrapiContentAccessibilityService
   */
  private applyAccessibilityFixes(): void {
    const container = this.elementRef.nativeElement.querySelector('.article-container');
    if (!container) return;

    const hasAppliedSomething = this.strapiAccessibility.applyAccessibilityFixes(container);
    if (hasAppliedSomething) {
      this.isAccessibilityApplied = true;
    }
  }

  ngOnDestroy(): void {
    this.subscriptionRemover.next();
    this.subscriptionRemover.complete();
  }

  private getArticles() {
    return this.newsService
      .retrieveArticles(
        qs.stringify({
          locale: this.locale,
          populate: {
            image: '*',
            localizations: {
              fields: ['slug', 'locale'],
            },
          },
          filters: {
            slug: {
              $eq: this.params,
            },
          },
        })
      )
      .pipe(
        switchMap((news) => {
          this.localization = news.data[0]?.attributes?.localizations?.data;
          return of(news.data[0]);
        })
      );
  }
}
